import {Injectable,} from '@angular/core';
import {SystemConstant,} from '@services/constant/system-constant';
import * as Encoding from 'encoding-japanese';

/**
 * 文字列ユーティリティー
 */
@Injectable({
  providedIn: 'root',
})
export class StringUtil {
  readonly C_ENC_AUTO: Encoding.Encoding = 'AUTO';
  readonly C_ENC_UNICODE: Encoding.Encoding = 'UNICODE';
  readonly C_ENC_UTF8: Encoding.Encoding = 'UTF8';
  readonly C_ENC_ISO2022JP: Encoding.Encoding = 'JIS';
  readonly C_ENC_SJIS: Encoding.Encoding = 'SJIS';

  /**
   * [一般開発者利用可]
   * 空文字チェック
   * @param str 文字列
   * @returns 空の場合true(null、undifineも空とみなす)
   */
  isEmpty(str: string | null | undefined): boolean {
    return str == null || str === '';
  }

  /**
   * [一般開発者利用可]
   * nullまたはundefinedの文字チェック
   * @param str 文字列
   * @returns null、undefinedの場合、trueを返す。
   */
  isNullOrUndefined(str: string | null | undefined): boolean {
    return str == null;
  }

  /**
   * [一般開発者利用可]
   * 両側トリム
   * @param str 文字列
   * @returs トリムした文字列
   */
  trim(str: string | null | undefined): string | null | undefined {
    if (this.isEmpty(str)) {
      return str;
    }
    return (<string>str).replace(/^ +| +$/g, '');
  }
  /**
   * [一般開発者利用可]
   * 右側トリム
   * @param str 文字列
   * @returs トリムした文字列
   */
  trimRight(str: string | null | undefined): string | null | undefined {
    if (this.isEmpty(str)) {
      return str;
    }
    return (<string>str).replace(/ +$/g, '');
  }

  /**
   * [一般開発者利用可]
   * 左側トリム
   * @param str 文字列
   * @returs トリムした文字列
   */
  trimLeft(str: string | null | undefined): string | null | undefined {
    if (this.isEmpty(str)) {
      return str;
    }
    return (<string>str).replace(/^ +/g, '');
  }

  /**
   * [一般開発者利用可]
   * 数字かどうか
   * @param str 検証文字列
   * @returns 数字検証の結果
   */
  isNumeric(str: string): boolean {
    if (this.isEmpty(this.trim(str))) {
      return false;
    }
    if (str.split('.').length > 2) {
      // InputString     => ステータス
      // '1.1.1'         => false
      return false;
    }
    str = str.replace(/^ +| +$/g, '');
    str = str.replace(/^　+|　+$/g, '');
    if (this.isEmpty(str)) {
      // InputString     => ステータス
      // '　'         => false
      return false;
    }
    if (str === '+' || str === '-' || str === '.'  || str === ',' || str === '，') {
      // InputString     => ステータス
      // '+'             => false
      // '-'             => false
      // '.'             => false
      // ','             => false
      // '，'             => false
      return false;
    }
    if (str.lastIndexOf(',') > -1 && str.indexOf('.') > -1 && str.lastIndexOf(',') > str.indexOf('.')) {
      // InputString     => ステータス
      // '99.55,66'      => false
      // '-99.55,66'     => false
      return false;
    }
    str = this.toHalfwidth(str).replace(/,+$/g, '');
    str = str.replaceAll('￥','¥');

    str = str.replaceAll('-', '+');
    if (str.split('\\').length > 2 || str.split('+').length > 2 || str.split('¥').length > 2) {
      // InputString     => ステータス
      // '123++'             => false
      // '+123+'             => false
      // '-123+'             => false
      // '+123-'             => false
      return false;
    }

    if (str.split('.').length > 2) {
      // InputString     => ステータス
      // '1.1.1'         => false
      return false;
    }

    if (str === '+' || str === '-' || str === '.' || str === '\\' || str === '¥') {
      // InputString     => ステータス
      // '＋'             => false
      // '－'             => false
      // '．'             => false
      // '\'             => false
      // '¥'             => false
      return false;
    }

    if (str === '\\+' || str === '+\\' || str === '¥+' || str === '+¥') {
      // InputString     => ステータス
      // '\+'             => false
      // '+\'             => false
      // '¥+'             => false
      // '+¥'             => false
      return false;
    }

    if (str.lastIndexOf(',') > -1 && str.indexOf('.') > -1 && str.lastIndexOf(',') > str.indexOf('.')) {
      // InputString     => ステータス
      // '99.55,66'      => false
      // '-99.55,66'     => false
      return false;
    }

    // InputString     => ステータス
    // '123+'     => true
    // '123-'     => true
    // '123￥'     => true
    str = str.replace(/\+$|-$|\\$|¥$|\+\\$|\\\+$/g, '');

    // InputString       => ステータス
    // ''                => false
    // ' '               => false
    // ',,,999,999.999'  => false
    // '999 err'         => false
    // '999999'          => true
    // '999,,,,999.999'  => true
    // '999999.999'      => true
    // '9'               => true
    // '0'               => true
    // ',9'              => false
    // '9,'              => true
    // '.9'              => true
    // '9.'              => true
    // '-,,,999,999.999' => false
    // '-999 err'        => false
    // '-999999'         => true
    // '-999,,,,999.999' => true
    // '-999999.999'     => true
    // '-9'              => true
    // '-0'              => true
    // '-,9'             => false
    // '-9,'             => true
    // '-.9'             => true
    // '-9.'             => true
    // '-.99999'         => true
    // '-9999,.'         => true
    // '-9999,9999.'     => true
    // '9999,.'          => true
    // '9999,9999.'      => true
    const regex = new RegExp('^(|-?|\\+?|\\\\?|¥?|\\+\\\\?|((\\\\)(\\+))?)([0-9.][0-9,.]+[0-9]|[0-9.]*|[0-9]+[0-9,]+[0-9.]+)$');
    return regex.test(str);
  }

  /**
   * [一般開発者利用可]
   * Stringを全角から半角に変換する。
   * @param str String
   * @return 変換されたString
   */
  toHalfwidth(str: string): string {
    const HALFWIDTH_KANA_MAP: {[key: number]: number} = {
      0x30a1: 0xff67,
      0x30a3: 0xff68,
      0x30a5: 0xff69,
      0x30a7: 0xff6a,
      0x30a9: 0xff6b,
      0x30e3: 0xff6c,
      0x30e5: 0xff6d,
      0x30e7: 0xff6e,
      0x30c3: 0xff6f,
      0x30fc: 0xff70,
      0x30a2: 0xff71,
      0x30a4: 0xff72,
      0x30a6: 0xff73,
      0x30a8: 0xff74,
      0x30aa: 0xff75,
      0x30ab: 0xff76,
      0x30ad: 0xff77,
      0x30af: 0xff78,
      0x30b1: 0xff79,
      0x30b3: 0xff7a,
      0x30b5: 0xff7b,
      0x30b7: 0xff7c,
      0x30b9: 0xff7d,
      0x30bb: 0xff7e,
      0x30bd: 0xff7f,
      0x30bf: 0xff80,
      0x30c1: 0xff81,
      0x30c4: 0xff82,
      0x30c6: 0xff83,
      0x30c8: 0xff84,
      0x30ca: 0xff85,
      0x30cb: 0xff86,
      0x30cc: 0xff87,
      0x30cd: 0xff88,
      0x30ce: 0xff89,
      0x30cf: 0xff8a,
      0x30d2: 0xff8b,
      0x30d5: 0xff8c,
      0x30d8: 0xff8d,
      0x30db: 0xff8e,
      0x30de: 0xff8f,
      0x30df: 0xff90,
      0x30e0: 0xff91,
      0x30e1: 0xff92,
      0x30e2: 0xff93,
      0x30e4: 0xff94,
      0x30e6: 0xff95,
      0x30e8: 0xff96,
      0x30e9: 0xff97,
      0x30ea: 0xff98,
      0x30eb: 0xff99,
      0x30ec: 0xff9a,
      0x30ed: 0xff9b,
      0x30ef: 0xff9c,
      0x30f3: 0xff9d,
      0x309b: 0xff9e,
      0x309c: 0xff9f,
      0x30f2: 0xff66,
    };

    let charArray: number[] = [];
    for (let i = 0; i < str.length; i++) {
      const charCode = str.charCodeAt(i);
      switch (true) {
        case charCode in HALFWIDTH_KANA_MAP:
          charArray.push(HALFWIDTH_KANA_MAP[charCode]);
          break;
        case 0x30ab <= charCode && charCode <= 0x30c9:
          charArray.push(HALFWIDTH_KANA_MAP[charCode - 1], 0xff9e);
          break;
        case 0x30cf <= charCode && charCode <= 0x30dd:
          charArray.push(HALFWIDTH_KANA_MAP[charCode - (charCode % 3)], [0xff9e, 0xff9f,][(charCode % 3) - 1]);
          break;
        case charCode === 0x30f4:
          charArray.push(0xff73, 0xff9e);
          break;
        default:
          charArray.push(charCode);
          break;
      }
    }

    str = String.fromCharCode.apply(null, charArray);
    charArray = [];
    for (let i = str.length - 1; 0 <= i; i--) {
      const charCode = (charArray[i] = str.charCodeAt(i));
      switch (true) {
        case charCode <= 0xff5e && 0xff01 <= charCode:
          charArray[i] -= 0xfee0;
          break;
        case charCode === 0x3000:
          charArray[i] = 0x0020;
          break;
        case charCode === 0x30fc:
          charArray[i] = 0x002d;
          break;
        default:
          break;
      }
    }

    return String.fromCharCode.apply(null, charArray);
  }

  /**
   * [一般開発者利用可]
   * Stringを半角から全角に変換する。
   * @param str String
   * @return 変換されたString
   */
  toFullwidth(str: string): string {
    const DAKUTEN = 0x309b;
    const HAN_DAKUTEN = 0x309c;
    const FULLWIDTH_KANA_MAP: {[key: number]: number} = {
      0xff66: 0x30f2,
      0xff67: 0x30a1,
      0xff68: 0x30a3,
      0xff69: 0x30a5,
      0xff6a: 0x30a7,
      0xff6b: 0x30a9,
      0xff6c: 0x30e3,
      0xff6d: 0x30e5,
      0xff6e: 0x30e7,
      0xff6f: 0x30c3,
      0xff70: 0x30fc,
      0xff71: 0x30a2,
      0xff72: 0x30a4,
      0xff73: 0x30a6,
      0xff74: 0x30a8,
      0xff75: 0x30aa,
      0xff76: 0x30ab,
      0xff77: 0x30ad,
      0xff78: 0x30af,
      0xff79: 0x30b1,
      0xff7a: 0x30b3,
      0xff7b: 0x30b5,
      0xff7c: 0x30b7,
      0xff7d: 0x30b9,
      0xff7e: 0x30bb,
      0xff7f: 0x30bd,
      0xff80: 0x30bf,
      0xff81: 0x30c1,
      0xff82: 0x30c4,
      0xff83: 0x30c6,
      0xff84: 0x30c8,
      0xff85: 0x30ca,
      0xff86: 0x30cb,
      0xff87: 0x30cc,
      0xff88: 0x30cd,
      0xff89: 0x30ce,
      0xff8a: 0x30cf,
      0xff8b: 0x30d2,
      0xff8c: 0x30d5,
      0xff8d: 0x30d8,
      0xff8e: 0x30db,
      0xff8f: 0x30de,
      0xff90: 0x30df,
      0xff91: 0x30e0,
      0xff92: 0x30e1,
      0xff93: 0x30e2,
      0xff94: 0x30e4,
      0xff95: 0x30e6,
      0xff96: 0x30e8,
      0xff97: 0x30e9,
      0xff98: 0x30ea,
      0xff99: 0x30eb,
      0xff9a: 0x30ec,
      0xff9b: 0x30ed,
      0xff9c: 0x30ef,
      0xff9d: 0x30f3,
      0xff9e: 0x309b,
      0xff9f: 0x309c,
    };

    let charArray = [];
    for (let i = str.length - 1; 0 <= i; i--) {
      const charCode = (charArray[i] = str.charCodeAt(i));
      switch (true) {
        case charCode <= 0x007e && 0x0021 <= charCode:
          charArray[i] += 0xfee0;
          break;
        case charCode === 0x0020:
          charArray[i] = 0x3000;
          break;
        default:
          break;
      }
    }

    str = String.fromCharCode.apply(null, charArray);
    charArray = [];
    for (let i = str.length - 1; 0 <= i; i--) {
      const charCode = str.charCodeAt(i);
      if (FULLWIDTH_KANA_MAP[charCode]) {
        charArray[i] = FULLWIDTH_KANA_MAP[charCode];
      } else {
        charArray[i] = charCode;
      }
    }

    str = String.fromCharCode.apply(null, charArray);
    charArray = [];
    for (let i = 0; i < str.length; i++) {
      const charCode = str.charCodeAt(i);
      switch (true) {
        case 0x304b <= charCode && charCode <= 0x3062 && charCode % 2 === 1:
        case 0x30ab <= charCode && charCode <= 0x30c2 && charCode % 2 === 1:
        case 0x3064 <= charCode && charCode <= 0x3069 && charCode % 2 === 0:
        case 0x30c4 <= charCode && charCode <= 0x30c9 && charCode % 2 === 0: {
          const nextChar = str.charCodeAt(i + 1);
          charArray.push(charCode + (nextChar === DAKUTEN ? 1 : 0));
          if (charArray[charArray.length - 1] !== charCode) i++;
          break;
        }
        case 0x306f <= charCode && charCode <= 0x307f && charCode % 3 === 0:
        case 0x30cf <= charCode && charCode <= 0x30dd && charCode % 3 === 0: {
          const nextChar = str.charCodeAt(i + 1);
          charArray.push(charCode + (nextChar === DAKUTEN ? 1 : nextChar === HAN_DAKUTEN ? 2 : 0));
          if (charArray[charArray.length - 1] !== charCode) i++;
          break;
        }
        case charCode === 0x3046 || charCode === 0x30a6: {
          const nextChar = str.charCodeAt(i + 1);
          charArray.push(charCode + (nextChar === DAKUTEN ? 78 : 0));
          if (charArray[charArray.length - 1] !== charCode) i++;
          break;
        }
        default:
          charArray.push(charCode);
          break;
      }
    }

    return String.fromCharCode.apply(null, charArray);
  }

  /**
   * [一般開発者利用可]
   * 文字列をバイト型配列に変換
   * @param strVal 文字列
   * @param enc Encodingタイプ
   * @returns UNICODEをEncodingに変換する
   */
   stringToByteArray(strVal: string, enc: Encoding.Encoding): number[] {
    return Encoding.convert(Encoding.stringToCode(strVal), {
      to: enc,
      from: this.C_ENC_UNICODE,
    });
  }

  /**
   * [一般開発者利用可]
   * 文字列をバイト型配列に変換
   * @param byteArray バイト型配列
   * @param enc Encodingタイプ
   * @returns EncodingをUNICODEに変換する
   */
  toUnicodeArray(byteArray: number[], enc: Encoding.Encoding): number[] {
    return Encoding.convert(byteArray, {
      to: this.C_ENC_UNICODE,
      from: enc,
    });
  }

  /**
   * [一般開発者利用可]
   * 文字列をバイト型配列に変換
   * @param strVal 文字列
   * @returns 文字をバイト配列(ASCII)に変換
   */
  getByteASCII(strVal: string): number[] {
    return Array.prototype.slice.call(Buffer.from(strVal, 'ascii'));
  }

  /**
   * [一般開発者利用可]
   * 文字列をバイト型配列に変換
   * @param strVal 文字列
   * @returns 文字をバイト配列(UTF-8)に変換
   */
  getByteUTF8(strVal: string): number[] {
    return this.stringToByteArray(strVal, this.C_ENC_UTF8);
  }

  /**
   * [一般開発者利用可]
   * 文字列をバイト型配列に変換
   * @param strVal 文字列
   * @returns 文字をバイト配列(ISO-2022-JP)に変換
   */
  getByteISO2022JP(strVal: string): number[] {
    return this.stringToByteArray(strVal, this.C_ENC_ISO2022JP);
  }

  /**
   * [一般開発者利用可]
   * 文字列をバイト型配列に変換
   * @param strVal 文字列
   * @returns 文字をバイト配列(Shift_JIS)に変換
   */
  getByteSJIS(strVal: string): number[] {
    return this.stringToByteArray(strVal, this.C_ENC_SJIS);
  }

  /**
   * [一般開発者利用可]
   * 指定された文字列のバイト数を返します。
   * @param strVal 文字列
   * @return バイト数
   */
   getByteCount(strVal: string, enc: Encoding.Encoding): number {
    return this.stringToByteArray(strVal, enc).length;
  }

  /**
   * [一般開発者利用可]
   * 指定された文字列のバイト数を返します。
   * @param strVal 文字列
   * @return バイト数
   */
  getByteCountUTF8(strVal: string): number {
    return this.getByteCount(strVal, this.C_ENC_UTF8);
  }

  /**
   * [一般開発者利用可]
   * 指定された文字列のバイト数を返します。
   * @param strVal 文字列
   * @return バイト数
   */
  getByteCountSJIS(strVal: string): number {
    return this.getByteCount(strVal, this.C_ENC_SJIS);
  }

  /**
   * [一般開発者利用可]
   * バイト配列よりバイトから文字に変換します。
   * @param byteArray バイト型配列
   * @param enc Encodingタイプ
   * @return 文字列 戻り値
   */
  getString(byteArray: number[], enc: Encoding.Encoding = 'AUTO'): string {
    return Encoding.codeToString(this.toUnicodeArray(byteArray, enc));
  }

  /**
   * [一般開発者利用可]
   * バイト配列よりバイトから文字に変換します。
   * @param byteArray バイト型配列
   * @param offset オフセット
   * @param length 取得するバイト数
   * @return 指定されたバイト位置から指定されたバイト数分の文字列。
   */
  getStringSpecify(byteArray: number[], offset: number, length: number): string {
    return this.getString(Object.assign([], byteArray).splice(offset, length));
  }

  /**
   * [一般開発者利用可]
   * 文字列内のひらがな文字をカタカナ文字に変換します。
   * VBAのStrConv関数（VbStrConv.Katakana）のTypeScript対応
   * @param strVal 変換対象の文字列
   * @return カタカナ文字に変換されたString
   */
  toKatakana(strVal: string | null | undefined): string {
    // ひらがな「ぁ」の文字コード
    const HIRAGANA_SMALL_A = 0x3041;
    // ひらがな「ゞ」の文字コード
    const HIRAGANA_VOICED_ITERATION_MARK = 0x309e;
    // ひらがな→カタカナ変換間隔
    const HIRAGANA_KATAKANA_STEP = 0x60;

    // 変換する文字列が空白の場合、変換されない
    if (this.isEmpty(strVal)) {
      return SystemConstant.EMPTY;
    }
    const charArray = [];
    if (strVal != null) {
      for (let i = 0; i < strVal.length; i++) {
        const charCode = strVal.charCodeAt(i);
        if (HIRAGANA_SMALL_A <= charCode && charCode <= HIRAGANA_VOICED_ITERATION_MARK) {
          charArray[i] = charCode + HIRAGANA_KATAKANA_STEP;
        } else {
          charArray[i] = charCode;
        }
      }
    }

    return String.fromCharCode.apply(null, charArray);
  }
}
